import { listOf } from '../entity/entity';
import { isInFlight } from './arc';
import type { Arc, Status } from './arc';

/**
 * What the board is showing.
 *
 * Filters are ADDITIVE — every one that is set has to pass — because that is
 * the only reading where adding a filter always narrows. A board where one
 * more choice can widen the result is a board nobody trusts.
 */
export interface Filters {
  query: string;
  area: string | null;
  tag: string | null;
  /** One status alone — the way to ask "what is waiting on me" and nothing else. */
  status: Status | null;
  /**
   * Being worked or waiting on you.
   *
   * Its own flag rather than a value of [status], because in flight is TWO
   * statuses and that field holds one. Widening it to take a set would make
   * every other reading of it ask which case it is in.
   */
  inFlight: boolean;
  /** `updated:` within the last seven days. */
  recent: boolean;
}

export const NO_FILTERS: Filters = {
  query: '',
  area: null,
  tag: null,
  status: null,
  inFlight: false,
  recent: false,
};

/**
 * The filter outlives the board's mount.
 *
 * Working through what is asking means opening an arc, coming back, changing a
 * status — and each of those rebuilt the board from scratch, so the narrowing
 * had to be set again and the row you were on found again by scrolling. Held
 * for the tab rather than stored: a new tab still opens on the whole board.
 */
let held: Filters = NO_FILTERS;

export function heldFilters(): Filters {
  return held;
}

export function holdFilters(next: Filters): void {
  held = next;
}

export function isFiltering(f: Filters): boolean {
  return (
    f.query.trim() !== '' ||
    f.area !== null ||
    f.tag !== null ||
    f.status !== null ||
    f.inFlight ||
    f.recent
  );
}

export function applyFilters(arcs: Arc[], f: Filters, now = new Date()): Arc[] {
  const query = f.query.trim().toLowerCase();
  const since = new Date(now);
  since.setDate(since.getDate() - 7);

  return arcs.filter((arc) => {
    if (f.area && String(arc.frontMatter['area'] ?? '') !== f.area) return false;
    if (f.tag && !listOf(arc.frontMatter['tags']).includes(f.tag)) return false;
    if (f.status && arc.status !== f.status) return false;
    if (f.inFlight && !isInFlight(arc.status)) return false;
    if (f.recent && !touchedSince(arc, since)) return false;
    if (!query) return true;
    // Title first, then the sentence that says what finishing looks like —
    // between them they are what someone is trying to find.
    return (
      arc.title.toLowerCase().includes(query) ||
      String(arc.frontMatter['done-when'] ?? '').toLowerCase().includes(query) ||
      String(arc.frontMatter['needs'] ?? '').toLowerCase().includes(query) ||
      String(arc.frontMatter['start-with'] ?? '').toLowerCase().includes(query) ||
      String(arc.frontMatter['scope'] ?? '').toLowerCase().includes(query)
    );
  });
}

function touchedSince(arc: Arc, since: Date): boolean {
  const raw = String(arc.frontMatter['updated'] ?? '');
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) >= since;
}

/** Every value the board could filter by, from what the arcs actually
 *  declare — a menu of options nothing carries is a menu of dead ends. */
export function facetsOf(arcs: Arc[]): { areas: string[]; tags: string[] } {
  const areas = new Set<string>();
  const tags = new Set<string>();
  for (const arc of arcs) {
    const area = String(arc.frontMatter['area'] ?? '').trim();
    if (area) areas.add(area);
    for (const tag of listOf(arc.frontMatter['tags'])) tags.add(tag);
  }
  return { areas: [...areas].sort(), tags: [...tags].sort() };
}
